import { inject, Injectable, InjectionToken } from "@angular/core";
import { getToken, MessagePayload, Messaging, onMessage } from "@angular/fire/messaging";
import { distinctUntilChanged, filter, firstValueFrom, ReplaySubject } from "rxjs";
import { AppService } from "./app.service";
import { HttpClient } from "@angular/common/http";
import { SearchResults } from "../model/common.model";
import { Router } from "@angular/router";

export type FcmConfig = {
  firebase: {
    apiKey: string, authDomain: string, projectId: string, storageBucket: string, messagingSenderId: string, appId: string, measurementId: string
  }, vapidKey: string
};

export const FCM_CONFIG = new InjectionToken<FcmConfig>('FCM_CONFIG');
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  // Firebase Messaging is only provided in the browser (it is not SSR-safe), so this is
  // injected optionally. Every consumer below runs only after the browser guard in the
  // constructor, where `messaging` is always present.
  private messaging: Messaging | null = inject(Messaging, { optional: true });
  private appService = inject(AppService);
  private config = inject(FCM_CONFIG);
  private http = inject(HttpClient);
  private router = inject(Router);
  

  token$ = new ReplaySubject<string>(1);
  permission$ = new ReplaySubject<NotificationPermission | undefined>(1);
  messages$ = new ReplaySubject<MessagePayload>(1);
  messagingChannel: BroadcastChannel;

  constructor() {
    if (!this.appService.isBrowser) return;

    this.checkPermission();

    this.configureBroadcastChannels();

  }

  private configureBroadcastChannels() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.messagingChannel = new BroadcastChannel('messsaging_broadcast_channel');
      this.messagingChannel.onmessage = (event: MessageEvent) => {
        console.debug('Message received from broadcast channel', event);
        const payload = event.data.payload as MessagePayload;
        this.handleMessage(payload);
      };
    }
  }

  private async checkPermission() {
    const permission = (typeof Notification === 'undefined') ? undefined : Notification.permission;
    this.permission$.next(permission);
    if (permission === 'granted') {
      await this.loadToken();
    }
  }

  async requestPermission() {
    try {
      const permission = await Notification.requestPermission();
      this.permission$.next(permission);
      if (permission === 'granted') {
        await this.loadToken();
      }
    } catch (e) {
      this.permission$.next(Notification.permission);
      this.token$.next(undefined);
    }
  }

  private async loadToken() {
    try {
      var reg = await this.getOrRegisterServiceWorker();

      console.debug('Getting FCM token', { messaging: this.messaging, config: this.config });
      const token = await getToken(this.messaging, { serviceWorkerRegistration: reg, vapidKey: this.config.vapidKey });
      console.debug('FCM Token', token);
      this.token$.next(token);

      console.debug('Listening to FCM messages...', this.messaging);
      onMessage(this.messaging, (payload) => {
        console.log('Message received. ', payload);
        this.handleMessage(payload);
      });

      await this.subscribe(token);

    } catch (e) {
      console.debug('Cannot get FCM token', e);
      this.token$.next(undefined);
    }
  }

  private messageIds = new Set<string>();
  private handleMessage(payload: MessagePayload) {
    console.debug('Handling message', payload);
    const messageId = payload.data?.['Id'];
    if (messageId && this.messageIds.has(messageId)) {
      console.debug('Message already handled', messageId);
      return;
    }

    // if (Notification.permission === 'granted' && payload.notification) {
    //   const notification = new Notification(payload.notification.title, { body: payload.notification.body, icon: payload.notification.icon });
    //   notification.onclick = () => {
    //     console.debug('Notification clicked', payload);
    //   };
    //   console.debug('Notification shown', notification);
    // }

    this.messages$.next(payload);
  }

  private async getOrRegisterServiceWorker() {
    var reg = (await navigator.serviceWorker.getRegistrations())
      .find(_ => _.active?.scriptURL?.includes('firebase-messaging-sw.js'));
    if (reg) {
      console.debug('Service Worker already registered', reg);
    } else {
      console.debug('Registering Service Worker');
      reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { type: 'module', scope: 'firebase-cloud-messaging-push-scope' });
      console.debug('Service Worker Registered...', reg);
    }
    return reg;
  }

  subscribe(token: string) {
    return firstValueFrom(this.http.post<any>(`/api/notification/push/subscribe/${token}`, null));
  }

  getSubscriptions(criteria: any) {
    return firstValueFrom(this.http.post<SearchResults<any>>(`/api/notification/push/subscriptions/search`, criteria));
  }

  adminNotify(notification: any) {
    return firstValueFrom(this.http.post<any>(`/api/notification/push/admin/notify`, notification));
  }

  getUserNotifications() {
    return firstValueFrom(this.http.post<any[]>(`/api/notification/user/notifications`, null));
  }

  markAsRead(ids: string[]) {
    return firstValueFrom(this.http.post<any[]>(`/api/notification/user/notification/read`, ids));
  }

  async showNotification(title: string, options: NotificationOptions, url: string) {
    if (!("Notification" in window)) {
      console.log("This browser does not support desktop notification");
      return;
    }

    console.debug('Requesting notification permission', Notification.permission);
    if (Notification.permission === "granted") {
      // If it's okay let's create a notification
      let notification = new Notification(title, options);
      let router = this.router;
      notification.onclick = function () {
        // bring the window in front and go to the url
        window.focus();
        if(url)
          router.navigateByUrl(url);
      }
      return;
    }

    if (Notification.permission === 'denied') 
      return;
    
    this.permission$.next('default');
  }
}